import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import { chatApi } from '../services/chatApi';
import ConversationList from '../components/ConversationList';
import ChatWindow from '../components/ChatWindow';
import NewConversationModal from '../components/NewConversationModal';
import { IoCreateOutline, IoSearchOutline, IoPersonCircleOutline, IoAlertCircleOutline, IoCloudOfflineOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import './Chat.css';

const Chat = () => {
  const { t } = useTranslation();
  const { conversationId: urlConversationId, messageId: urlMessageId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const messageIdParam = urlMessageId || searchParams.get('messageId');
  const { conversations, activeConversationId, setActiveConversationId, loading, hasConversationsLoaded, loadMessagesAround, setJumpToMessageId, connectionStatus, networkError, loadConversations } = useChat();

  // Show skeletons only when loading AND no conversations have been loaded yet
  const showConversationSkeletons = loading && !hasConversationsLoaded;
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const linkLoadingRef = useRef(false);

  // Cross-chat search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Handle message link - navigate to specific message from URL
  // Supports both /chat/:conversationId?messageId=xxx and /chat-link/:shortCode
  useEffect(() => {
    const handleMessageLink = async () => {
      // Need either (urlConversationId + messageIdParam) OR just urlMessageId (for /chat-link route)
      const hasOldFormat = urlConversationId && messageIdParam;
      const hasNewFormat = urlMessageId && !urlConversationId;

      if ((!hasOldFormat && !hasNewFormat) || linkLoadingRef.current) return;

      linkLoadingRef.current = true;
      setLinkLoading(true);
      setLinkError(null);

      try {
        let message;
        let actualMessageId;

        if (hasNewFormat) {
          // Short code format: /chat-link/:shortCode
          const resolveResponse = await chatApi.resolveMessageLink(urlMessageId);
          if (!resolveResponse.success) {
            setLinkError(resolveResponse.error || 'Message not found');
            setLinkLoading(false);
            linkLoadingRef.current = false;
            return;
          }
          message = resolveResponse.data;
          actualMessageId = message.id;
        } else {
          // UUID format: /chat/:id?messageId=xxx
          const msgResponse = await chatApi.getMessage(messageIdParam);
          if (!msgResponse.success) {
            setLinkError(msgResponse.error || 'Message not found');
            setLinkLoading(false);
            linkLoadingRef.current = false;
            return;
          }
          message = msgResponse.data;
          actualMessageId = messageIdParam;
        }

        const convId = message.conversation_id;

        // Set the active conversation
        setActiveConversationId(convId);
        setMobileShowChat(true);

        // Clear the query param after handling (to avoid re-triggering)
        setSearchParams({}, { replace: true });

        // Jump to the message after a short delay to let messages load
        setTimeout(() => {
          setJumpToMessageId(actualMessageId);
          linkLoadingRef.current = false;
        }, 500);

      } catch (err) {
        console.error('Failed to load message link:', err);
        setLinkError('Failed to load message');
        setLinkLoading(false);
        linkLoadingRef.current = false;
      }
    };

    handleMessageLink();
  }, [urlConversationId, urlMessageId, messageIdParam, setActiveConversationId, setJumpToMessageId, setSearchParams]);

  const activeConversation = conversations.find(c => c.id === activeConversationId);

  const handleSelectConversation = (conv) => {
    setActiveConversationId(conv.id);
    setMobileShowChat(true);
  };

  const handleBack = () => {
    setMobileShowChat(false);
  };

  const handleConversationCreated = (conv) => {
    setActiveConversationId(conv.id);
    setMobileShowChat(true);
    setShowNewConversation(false);
  };

  // Cross-chat search handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await chatApi.searchAllMessages(query, 20, 0);
        const results = response?.data || response || [];
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSearchResultClick = async (result) => {
    // Clear search state first
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);

    // Load messages around the target message FIRST (before changing conversation)
    await loadMessagesAround(result.conversation_id, result.id, 20, 20);

    // Then navigate to the conversation (this will show ChatWindow)
    setActiveConversationId(result.conversation_id);
    setMobileShowChat(true);

    // Set the target message ID for highlighting/scrolling in ChatWindow
    // Use setTimeout to ensure the conversation change has propagated
    setTimeout(() => {
      setJumpToMessageId(result.id);
    }, 100);
  };

  const handleCloseSearch = () => {
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSearchResults && !e.target.closest('.chat-search-container')) {
        handleCloseSearch();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  // Clean up active conversation on unmount
  useEffect(() => {
    return () => setActiveConversationId(null);
  }, [setActiveConversationId]);

  // Helper to get conversation name for search results
  // Uses API data first (from search response), falls back to conversations array
  const getConversationName = (msg) => {
    // First check if API returned conversation info
    if (msg.conversation_name) return msg.conversation_name;
    // Fall back to looking up in conversations
    const conv = conversations.find(c => c.id === msg.conversation_id);
    if (!conv) return 'Unknown';
    if (conv.is_group) return conv.name;
    return conv.other_user?.display_name || conv.other_user?.username || 'Unknown';
  };

  // Helper to get conversation avatar for search results
  const getConversationAvatar = (msg) => {
    // First check if API returned conversation avatar
    if (msg.conversation_avatar) return msg.conversation_avatar;
    // Fall back to looking up in conversations
    const conv = conversations.find(c => c.id === msg.conversation_id);
    if (!conv) return null;
    if (conv.is_group) return conv.avatar_url;
    return conv.other_user?.avatar_url;
  };

  // Helper to format time for search results
  const formatSearchTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Helper to highlight search term in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
    );
  };

  return (
    <div className="chat-page">
      <div className="chat-layout">
        <div className={`chat-sidebar ${mobileShowChat ? 'mobile-hidden' : ''}`}>
          <div className="chat-sidebar-header">
            <h2>{t('chat.messages')}</h2>
            <button
              className="icon-button"
              onClick={() => setShowNewConversation(true)}
              data-tooltip={t('chat.newConversation')}
            >
              <IoCreateOutline />
            </button>
          </div>

          {/* Connection status indicator */}
          {connectionStatus === 'disconnected' && (
            <div className="connection-status disconnected">
              <IoCloudOfflineOutline />
              <span>{t('chat.disconnected')}</span>
            </div>
          )}

          {/* Message link error */}
          {linkError && (
            <div className="chat-link-error">
              <IoAlertCircleOutline />
              <span>{linkError}</span>
            </div>
          )}

          {/* Cross-chat search */}
          <div className="chat-search-container">
            <div className="chat-search-input-wrapper">
              <IoSearchOutline className="chat-search-icon" />
              <input
                type="text"
                className="chat-search-input"
                placeholder={t('chat.searchAllMessages')}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {showSearchResults && (
              <div className="chat-search-results">
                {searching ? (
                  <div className="chat-search-loading">{t('common.loading')}</div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="chat-search-results-header">
                      {searchResults.length} {t('chat.searchResults')}
                    </div>
                    {searchResults.map((msg) => {
                      const avatarUrl = getConversationAvatar(msg);
                      return (
                        <div
                          key={msg.id}
                          className="chat-search-result-item"
                          onClick={() => handleSearchResultClick(msg)}
                        >
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt=""
                              className="chat-search-result-avatar"
                            />
                          ) : (
                            <div className="chat-search-result-avatar-placeholder">
                              <IoPersonCircleOutline />
                            </div>
                          )}
                          <div className="chat-search-result-content">
                            <div className="chat-search-result-header">
                              <span className="chat-search-result-conversation">
                                {getConversationName(msg)}
                              </span>
                              <span className="chat-search-result-time">
                                {formatSearchTime(msg.created_at)}
                              </span>
                            </div>
                            <span className="chat-search-result-preview">
                              {highlightText(msg.content?.substring(0, 100), searchQuery)}
                              {msg.content?.length > 100 ? '...' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="chat-search-empty">{t('chat.noSearchResults')}</div>
                )}
              </div>
            )}
          </div>
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={handleSelectConversation}
            loading={showConversationSkeletons}
            error={networkError}
            onRetry={loadConversations}
          />
        </div>

        <div className={`chat-main ${!mobileShowChat ? 'mobile-hidden' : ''}`}>
          {activeConversation ? (
            <ChatWindow
              conversation={activeConversation}
              onBack={handleBack}
            />
          ) : (
            <div className="chat-empty-state">
              <IoCreateOutline className="chat-empty-icon" />
              <p>{t('chat.selectConversation')}</p>
            </div>
          )}
        </div>
      </div>

      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          onCreated={handleConversationCreated}
        />
      )}
    </div>
  );
};

export default Chat;
